import { Badge, Box, Flex, HStack, Icon, SimpleGrid, Text, VStack } from '@chakra-ui/react';
import { useState } from 'react';
import { IconType } from 'react-icons';
import { MdAutoFixHigh, MdEditNote, MdHub } from 'react-icons/md';
import { FiArrowLeft, FiChevronRight } from 'react-icons/fi';
import { HelivanColor } from '../helivan/HelivanColor';
import { CompactionOptimizer } from './CompactionOptimizer';
import { InjectionOptimizer } from './InjectionOptimizer';

interface SteerHubProps {
  // The watch's coding agent + the session it's pinned to.
  agentId?: string;
  sessionId?: string | null;
  // The watch's fixed task count (for the cost estimate).
  taskCount?: number;
  // The watch's completed versions (for steering toward a previous version).
  timelineProjects?: Array<{ id: string; dateCreated: string }>;
}

type ToolKey = 'compaction' | 'inject-correction';

interface SteerTool {
  key: ToolKey | string;
  icon: IconType;
  title: string;
  description: string;
  available: boolean;
}

const TOOLS: SteerTool[] = [
  {
    key: 'compaction',
    icon: MdAutoFixHigh,
    title: 'Compaction optimizer',
    description: "Recommend the best /compact message to preserve — or restore — your agent's plan.",
    available: true,
  },
  {
    key: 'inject-correction',
    icon: MdEditNote,
    title: 'Inject a correction',
    description: 'Recommend a mid-session message that pulls the agent back toward an earlier version.',
    available: true,
  },
  {
    key: 'coordinate',
    icon: MdHub,
    title: 'Coordinate agents',
    description: 'Optimize coordination across multiple agents working together.',
    available: false,
  },
];

// The Steer tab is an extensible hub of steering tools. The compaction optimizer is the
// first active tool; future tools slot in as additional cards.
export const SteerHub = ({ agentId, sessionId, taskCount, timelineProjects }: SteerHubProps) => {
  const [openTool, setOpenTool] = useState<ToolKey | null>(null);

  if (openTool) {
    return (
      <Flex width="full" flexDirection="column" maxWidth="900px" mx="auto" gap={4}>
        <HStack
          spacing={2}
          color={HelivanColor.text.blueMid}
          cursor="pointer"
          alignSelf="flex-start"
          _hover={{ color: HelivanColor.text.helivanBlue }}
          onClick={() => setOpenTool(null)}
        >
          <FiArrowLeft size={15} />
          <Text fontSize="13px" fontWeight={600}>
            All steering tools
          </Text>
        </HStack>
        {openTool === 'compaction' ? (
          <CompactionOptimizer
            agentId={agentId}
            sessionId={sessionId}
            taskCount={taskCount}
            timelineProjects={timelineProjects}
          />
        ) : (
          <InjectionOptimizer
            agentId={agentId}
            sessionId={sessionId}
            taskCount={taskCount}
            timelineProjects={timelineProjects}
          />
        )}
      </Flex>
    );
  }

  return (
    <Flex width="full" flexDirection="column" maxWidth="900px" mx="auto">
      <VStack align="start" spacing={0} mb={5}>
        <Text fontSize="18px" fontWeight={700} color={HelivanColor.text.blueDark}>
          Steer
        </Text>
        <Text fontSize="14px" color={HelivanColor.text.blueMid}>
          Directly alter agent behavior based on a previous or ideal state.
        </Text>
      </VStack>

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        {TOOLS.map((tool) => (
          <Box
            key={tool.key}
            bg="white"
            border="1px solid"
            borderColor={HelivanColor.border.primary}
            borderRadius="12px"
            p={5}
            cursor={tool.available ? 'pointer' : 'default'}
            opacity={tool.available ? 1 : 0.6}
            transition="all 0.15s ease"
            _hover={tool.available ? { borderColor: HelivanColor.text.helivanBlue, boxShadow: '0 1px 6px rgba(0,0,0,0.08)' } : {}}
            onClick={() => tool.available && setOpenTool(tool.key as ToolKey)}
          >
            <Flex justify="space-between" align="start" gap={3}>
              <HStack spacing={3} align="start">
                <Flex
                  w="40px"
                  h="40px"
                  borderRadius="10px"
                  bg={HelivanColor.hover.lightBlue}
                  align="center"
                  justify="center"
                  flexShrink={0}
                >
                  <Icon as={tool.icon} boxSize="20px" color={HelivanColor.text.helivanBlue} />
                </Flex>
                <VStack align="start" spacing={1}>
                  <HStack spacing={2}>
                    <Text fontSize="15px" fontWeight={600} color={HelivanColor.text.blueDark}>
                      {tool.title}
                    </Text>
                    {!tool.available && (
                      <Badge colorScheme="gray" fontSize="10px">
                        Coming soon
                      </Badge>
                    )}
                  </HStack>
                  <Text fontSize="13px" color={HelivanColor.text.blueMid} lineHeight="1.5">
                    {tool.description}
                  </Text>
                </VStack>
              </HStack>
              {tool.available && (
                <Box color={HelivanColor.text.blueMid} mt={1}>
                  <FiChevronRight size={18} />
                </Box>
              )}
            </Flex>
          </Box>
        ))}
      </SimpleGrid>
    </Flex>
  );
};
